import { useEffect } from 'react';
import { FunctionComponent, useMemo, useState } from 'react';
import { Autocomplete, TextField, LinearProgress, Typography, Card, Divider, Dialog, SelectMultiple } from '../../tailwindcomponents';
import LineWorkpackageCard from "../Line/LineWorkpackageCard";
import CrewPlanDialog from '../CrewPlan/CrewPlanDialog';
import { LineWorkPackages } from './../../types/line';
import { MxiStaffManagementEmployee } from './../../types/staffManagement';
import crewPlanApi from '../../api/crewPlan';
import { formatIcelandicDateTime, parseDateTimeToTimestamp } from '../../utils/dateUtils';

type TaskProps = {
    myCrew: Array<MxiStaffManagementEmployee>,
    allTails: Array<string>,
    tailFilter: string,
    onTailFilterChange: (tail: string) => void,
    mockTasks?: Array<LineWorkPackages>,
}

const statusOptions = [
    { id: 0, name: "COMPLETE" },
    { id: 1, name: "ACTV" },
    { id: 2, name: "CANCEL" },
    { id: 3, name: "IN WORK" },
    { id: 4, name: "PAUSE" },
];


const MyTaskPlan: FunctionComponent<TaskProps> = ({ myCrew, allTails, tailFilter, onTailFilterChange, mockTasks }) => {
    const [taskBayDialogOpen, setTaskBayDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<LineWorkPackages>();
    const [tasks, setTasks] = useState<Array<LineWorkPackages>>([]);
    const [allTasks, setAllTasks] = useState<Array<LineWorkPackages>>([]);
    const [selectedOptions, setSelectedOptions] = useState<number[]>([1, 3, 4]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (mockTasks && mockTasks.length > 0) {
            setAllTasks(mockTasks);
            setLoading(false);
            return;
        }

        crewPlanApi.GetTasksAssignedToMe().then(res => {
            if (res.status) {
                const array = [] as Array<LineWorkPackages>;
                res.data.forEach(s => {
                    const taskDetails = s.task_details;
                    const task = {
                        wo_barcode: taskDetails.wo_barcode,
                        wo_start_time: taskDetails.wo_start_time ?? s.wo_start_time,
                        task_barcode: taskDetails.barcode,
                        is_unassigned: taskDetails.is_unassigned,
                        status: taskDetails.status,
                        task_name: s.title,
                        mxi_start_date_text: s.start_date_text,
                        mxi_end_date_text: s.end_date_text,
                        is_task_critical: taskDetails.is_task_critical,
                        assigned_employees: taskDetails.assigned_employees_on_calendar,
                        wo_name: taskDetails.wo_name,
                        inventory: s.inventory,
                        tail_number: taskDetails.tail_number,
                    } as LineWorkPackages;
                    array.push(task);
                });
                const tempSelectedStatus: string[] = [];
                selectedOptions.forEach(index => {
                    tempSelectedStatus.push(statusOptions[index].name)
                });
                setTasks(array.filter(s => tempSelectedStatus.includes(s.status)));
                setAllTasks(array);
            }
            setLoading(false);
        });
    }, [mockTasks]);

    useEffect(() => {
        const tempSelectedStatus: string[] = [];
        selectedOptions.forEach(index => {
            tempSelectedStatus.push(statusOptions[index].name);
        });
        const filteredByStatus = allTasks.filter(s => tempSelectedStatus.includes(s.status));
        const filteredByStatusAndTail = tailFilter ? filteredByStatus.filter(s => s.tail_number === tailFilter) : filteredByStatus;
        setTasks(filteredByStatusAndTail);
    }, [selectedOptions, tailFilter, allTasks]);

    const onSelectTask = (taskBarcode: string) => {
        setSelectedEvent(tasks.find(s => s.task_barcode === taskBarcode));
        setTaskBayDialogOpen(true);
    }

    const groupedTasks = useMemo(() => {
        const groups: Array<{ woBarcode: string; woName: string; tailNumber: string; woStartTime?: string; earliestTaskTimestamp: number; tasks: Array<LineWorkPackages> }> = [];
        const groupByBarcode = new Map<string, { woBarcode: string; woName: string; tailNumber: string; woStartTime?: string; earliestTaskTimestamp: number; tasks: Array<LineWorkPackages> }>();

        tasks.forEach((task) => {
            const taskTimestamp = parseDateTimeToTimestamp(task.mxi_start_date_text);
            const existingGroup = groupByBarcode.get(task.wo_barcode);
            if (existingGroup) {
                existingGroup.tasks.push(task);
                existingGroup.earliestTaskTimestamp = Math.min(existingGroup.earliestTaskTimestamp, taskTimestamp);
                if (!existingGroup.woStartTime) {
                    existingGroup.woStartTime = task.wo_start_time;
                }
                return;
            }

            const newGroup = {
                woBarcode: task.wo_barcode,
                woName: task.wo_name,
                tailNumber: task.tail_number,
                woStartTime: task.wo_start_time,
                earliestTaskTimestamp: taskTimestamp,
                tasks: [task],
            };
            groupByBarcode.set(task.wo_barcode, newGroup);
            groups.push(newGroup);
        });

        groups.forEach((group) => {
            group.tasks.sort((a, b) => parseDateTimeToTimestamp(a.mxi_start_date_text) - parseDateTimeToTimestamp(b.mxi_start_date_text));
        });

        return groups.sort((a, b) => {
            const woStartA = parseDateTimeToTimestamp(a.woStartTime);
            const woStartB = parseDateTimeToTimestamp(b.woStartTime);
            if (woStartA !== woStartB) {
                return woStartA - woStartB;
            }

            return a.earliestTaskTimestamp - b.earliestTaskTimestamp;
        });
    }, [tasks]);

    return (
        <div>
            {loading ?
                <LinearProgress />
                :
                <div>
                    <div className="mb-4 flex gap-4">
                        <Autocomplete
                            fullWidth
                            noOptionsText="No tails found"
                            options={allTails}
                            value={tailFilter}
                            clearOnEscape
                            autoHighlight
                            onChange={(e: any, newValue: string | null) => onTailFilterChange(newValue ?? "")}
                            renderInput={(params) => (
                                <TextField {...params} label="Filter by Tail" variant="standard" />
                            )}
                            className="max-w-[200px]"
                        />
                        <SelectMultiple
                            optionsObject={statusOptions}
                            onSelectedItem={(e: any) => setSelectedOptions(e.target.value)}
                            selectedItems={selectedOptions}
                            width={200}
                            filter_by_text={"Filter by status"}
                        />
                    </div>
                    {
                        tasks.length > 0 ?
                            <div>
                                <Typography text={`Tasks (${tasks.length})`} variant='h3' align='center' margin={[0, 0, 0, 0]} fontWeight='bold' />
                                {groupedTasks.map((group, groupIndex) => (
                                    <div key={group.woBarcode} className="mb-4">
                                        <Typography
                                            text={`${group.woName} (${group.tasks.length}) - ${group.tailNumber} - ${formatIcelandicDateTime(group.woStartTime)}`}
                                            variant='h4'
                                            margin={[0, 0, 1, 0]}
                                            fontWeight='bold'
                                        />
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {group.tasks.map((task) => (
                                                <div key={task.task_barcode}>
                                                    <Card>
                                                        <LineWorkpackageCard type='BAY-USER' task={task} onOpenDialogClicked={(taskBarcode: string) => { onSelectTask(taskBarcode) }} />
                                                    </Card>
                                                </div>
                                            ))}
                                        </div>
                                        {groupIndex < groupedTasks.length - 1 && (
                                            <Divider className="mt-4 border-b-2 border-[color:var(--ds-border-strong)]" />
                                        )}
                                    </div>
                                ))}
                            </div>
                            :
                            <Typography text={`No tasks assigned`} color={'primary'} variant={'h2'} align={'center'} margin={[0, 0, 1, 0]} />
                    }
                </div>
            }
            <Dialog
                visible={taskBayDialogOpen}
                max_width={"lg"}
                title={selectedEvent?.title}
                context={
                    <CrewPlanDialog myCrew={myCrew} selectedEvent={selectedEvent} />
                }
                onClose={(status: any) => { setTaskBayDialogOpen(false) }}
                strict={false}
                show_options={false}
            />
        </div>
    )
}

export default MyTaskPlan;
